import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';

const ModernNavbar = () => {
  const { t } = useTranslation();
  const { language, changeLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    // Prevent body scroll when menu is open
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  
  const languageButtons = [
    { code: 'fr', label: 'FR' },
    { code: 'en', label: 'EN' },
    { code: 'ar', label: 'عربي' }
  ];

  return (
    <nav className={`modern-nav ${isScrolled ? 'scrolled' : ''}`}>
      <div className="container modern-nav-container">
        <Link to="/" className="modern-nav-logo">
          <img 
            src={isScrolled ? "/images/assco-black.png" : "/images/assco-white.png"} 
            alt="ASSCO Logo" 
            style={{ 
              height: '60px', 
              marginRight: '10px',
              transition: 'all 0.3s ease'
            }} 
            className="header-logo"
          />
        </Link>
        
        <button 
          className="modern-nav-toggle" 
          aria-label="Toggle menu"
          onClick={toggleMenu}
        >
          {isMenuOpen ? '✕' : '☰'}
        </button>
        
        <div 
          ref={menuRef}
          className={`modern-nav-menu ${isMenuOpen ? 'open' : ''}`}
        >
          <div className="mobile-nav-header">
            <img 
              src="/images/assco-white.png" 
              alt="ASSCO Logo" 
              style={{ 
                height: '30px', 
                marginBottom: '20px',
                display: 'none'
              }} 
              className="mobile-logo"
            />
            <button 
              className="mobile-close-btn"
              onClick={toggleMenu}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: 'var(--assco-gray-900)',
                display: 'none'
              }}
            >
              ✕
            </button>
          </div>
          
          <Link to="/" className="modern-nav-link" onClick={() => setIsMenuOpen(false)}>
            {t('navigation.home')}
          </Link>
          <Link to="/about" className="modern-nav-link" onClick={() => setIsMenuOpen(false)}>
            {t('navigation.about')}
          </Link>
          <Link to="/services" className="modern-nav-link" onClick={() => setIsMenuOpen(false)}>
            {t('navigation.services')}
          </Link>
          <Link to="/products" className="modern-nav-link" onClick={() => setIsMenuOpen(false)}>
            {t('navigation.products')}
          </Link>
          <Link to="/projects" className="modern-nav-link" onClick={() => setIsMenuOpen(false)}>
            {t('navigation.projects')}
          </Link>
          
          {/* Language Switcher */}
          <div className="language-switcher" style={{ 
            display: 'flex', 
            gap: '0.5rem',
            marginLeft: '1rem',
            borderLeft: '1px solid rgba(255, 255, 255, 0.2)',
            paddingLeft: '1rem',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            {languageButtons.map(({ code, label }) => (
              <button
                key={code}
                onClick={() => {
                  changeLanguage(code);
                  setIsMenuOpen(false);
                }}
                style={{
                  padding: '0.5rem',
                  backgroundColor: language === code ? 'var(--assco-orange)' : 'transparent',
                  color: language === code ? 'white' : isScrolled ? 'var(--assco-gray-700)' : 'rgba(255, 255, 255, 0.8)',
                  borderRadius: 'var(--radius-md)',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                  transition: 'all 0.3s'
                }}
              >
                {label}
              </button>
            ))}
          </div>
          
          <Link to="/contact" className="modern-nav-cta" onClick={() => setIsMenuOpen(false)}>
            {t('navigation.contact')}
          </Link>
        </div>
        
        {/* Mobile menu overlay */}
        {isMenuOpen && (
          <div 
            className="mobile-menu-overlay"
            onClick={() => setIsMenuOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 49,
              display: 'none'
            }}
          />
        )}
      </div>
    </nav>
  );
};

export default ModernNavbar;